import type { ReactNode } from 'react';
import { WorkspaceIcon } from './icons';
import { ConnectionMark } from './your-connections/connection-mark';
import type { ConnectionRowState } from './your-connections/rows';

/**
 * Where a catalogue entry stands for this person. The tile's one button always says it: Connect,
 * Connected, Sign in again, Turned off, or why it cannot be connected here.
 */
export type CatalogueTileState =
  | 'connect'
  | 'busy'
  | 'connected'
  | 'attention'
  | 'off'
  | 'unavailable'
  | 'not_allowed';

/** A connected account behind a catalogue entry, as the Connections page knows it. */
export type CatalogueConnection = {
  state: ConnectionRowState;
  identity: string | null;
  open?: () => void;
  signIn?: () => void;
};

export const tileStateFor = (state: ConnectionRowState): CatalogueTileState =>
  state === 'connected'
    ? 'connected'
    : state === 'off'
      ? 'off'
      : state === 'unavailable'
        ? 'unavailable'
        : 'attention';

type CardAction = { label: string; disabled?: boolean; onClick?: () => void };

/** The button of a tile behind which this person already has an account. */
export function connectedTileAction(
  state: CatalogueTileState,
  connection: CatalogueConnection,
  copy: { tileSignIn: string; tileOff: string; tileUnavailable: string; tileConnected: string },
  busy: boolean,
): CardAction {
  const signIn = state === 'attention' ? connection.signIn : undefined;
  return {
    label: signIn
      ? copy.tileSignIn
      : state === 'off'
        ? copy.tileOff
        : state === 'unavailable'
          ? copy.tileUnavailable
          : copy.tileConnected,
    // While any connect is under way, no tile starts another or opens a panel over it.
    disabled: busy,
    onClick: signIn ?? connection.open,
  };
}

/** The tile every integration in the catalogue renders through, whoever provides it. */
export function CatalogueCard({
  source,
  logo,
  logoId,
  name,
  subtitle,
  description,
  state,
  action,
  manage,
}: {
  /** Who runs the connection; the tile looks the same either way. */
  source: 'pipedream' | 'poneva' | 'customer-app' | 'native';
  /** Image address; the tile falls back to a vendored mark, then the name's first letter. */
  logo?: string | null;
  logoId?: string;
  name: string;
  /** One line: the connected account, or what the app is. */
  subtitle?: ReactNode;
  /** The provider's description, offered as the tile's tooltip. */
  description?: string;
  state: CatalogueTileState;
  action: CardAction;
  /** Replaces the button, e.g. an admin's allow switch while choosing what members can connect. */
  manage?: ReactNode;
}) {
  const badge = state === 'connected' || state === 'attention' || state === 'off' ? state : null;
  return (
    <article className="ws-pipedream-card" data-source={source} data-state={state}>
      <ConnectionMark name={name} src={logo ?? null} logoId={logoId} state={badge} />
      <div className="ws-pipedream-card-text" title={description || undefined}>
        <h3>{name}</h3>
        {subtitle && <p className="ws-pipedream-subtitle">{subtitle}</p>}
      </div>
      {manage ??
        (action.onClick ? (
          <button
            type="button"
            className="ws-pipedream-connect"
            data-state={state}
            disabled={action.disabled || state === 'busy'}
            aria-busy={state === 'busy' || undefined}
            onClick={action.onClick}
          >
            <TileStateIcon state={state} />
            {action.label}
          </button>
        ) : (
          <span className="ws-pipedream-connect" data-state={state}>
            <TileStateIcon state={state} />
            {action.label}
          </span>
        ))}
    </article>
  );
}

function TileStateIcon({ state }: { state: CatalogueTileState }) {
  if (state === 'busy') return <span className="ws-pipedream-spinner" aria-hidden="true" />;
  if (state === 'connected') return <WorkspaceIcon name="check" size={13} />;
  return null;
}
